import {FC, useMemo} from "react";
import {Box, Breadcrumbs, Stack, Tab, Tabs, Typography, Skeleton} from "@mui/material";
import {Link, Navigate, Route, Routes, useLocation, useNavigate, useParams} from "react-router-dom";
import {
	UserTrainingFeedTab,
	UserActiveCoursesTab,
	UserActivePlaylistsTab,
	UserFinishedCoursesTab,
	UserTrainingPoolTab,
	UserRecentActivityTab
} from "./tabs";
import {useGetUser} from "@/app2/shared/data/repos/users";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";

/**
 * Admin User Page
 *
 * Shows a tabbed view of a user's training data:
 * - Training Feed (pending lessons)
 * - Training Pool (recommended courses)
 * - Watch History (recent activity)
 * - Active Courses
 * - Finished Courses
 */
export const AdminUserPage: FC = () => {
	const {id: _userId} = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const userId = _userId ? parseInt(_userId, 10) : undefined;
	const {data: user} = useGetUser(userId);	
	const fullName = `${user?.firstName ?? 'User'} ${user?.lastName ?? ''}`;
	const {playlists: playlistsEnabled} = useDevCycleSdk();

	// Tab configuration
	const tabs = useMemo(() => {
		const base: {label: string; path: string}[] = [
			{label: `${fullName}'s Homepage`, path: "training-feed"},
			{label: "Training Pool", path: "training-pool"},
			{label: "Watch History", path: "watch-history"},
			{label: "Active Courses", path: "active-courses"}
		];
		if (playlistsEnabled) {
			base.push({label: "Active Playlists", path: "active-playlists"});
		}
		base.push({label: "Finished Courses", path: "finished-courses"});
		return base;
	}, [fullName, playlistsEnabled]);

	// Determine current tab from URL - default to 0 if not found
	const currentTab = useMemo(() => {
		const currentPath = location.pathname.split("/").pop();
		const index = tabs.findIndex((tab) => tab.path === currentPath);
		return index >= 0 ? index : 0;
	}, [location.pathname, tabs]);

	const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
		navigate(tabs[newValue].path);
	};

	if (!userId) {
		return <Navigate to="../" replace />;
	}

	return (
		<Box>
			{/* Breadcrumbs */}
			<Breadcrumbs separator="/" sx={{mb: 2}}>
				<Link to="/admin/users" style={{textDecoration: "none", color: "inherit"}}>
					Employees
				</Link>
				<Typography color="text.primary">{fullName}</Typography>
			</Breadcrumbs>

			{/* Header */}	
			<Stack direction="row" alignItems={ user ? "baseline" : "end" } spacing={2} sx={{mb: 3}}>
			{
				user ? (
					<>
						<Typography variant="h1">{fullName}</Typography>
						<Typography variant="subtitle2" sx={{color: "white"}}>{`${user?.streak} day${user?.streak === 1 ? "" : "s"} streak`}</Typography>
					</>
				) : (
					<>
						<Stack justifyContent={"flex-end"}>
							<Skeleton variant="text" width="250px" height="80px" />
						</Stack>
						<Stack justifyContent={"flex-end"}>
							<Skeleton variant="text" width="100px" height="30px" sx={{ marginBottom: "10px" }} />
						</Stack>							
					</>
				)
			}
			</Stack>
			{/* Tabs */}
			<Tabs
				value={currentTab}
				onChange={handleTabChange}
				sx={{mb: 3, borderBottom: 1, borderColor: "divider"}}
			>
				{tabs.map((tab) => (
					<Tab
						key={tab.path}
						label={tab.label}
						sx={{
							textTransform: "uppercase",
							fontSize: "0.875rem",
							letterSpacing: "0.4px"
						}}
					/>
				))}
			</Tabs>

			{/* Tab Content */}
			<Routes>
				<Route path="/" element={<Navigate to="training-feed" replace />} />
				<Route path="training-feed" element={<UserTrainingFeedTab userId={userId} />} />
				<Route path="training-pool" element={<UserTrainingPoolTab userId={userId} />} />
				<Route path="watch-history" element={<UserRecentActivityTab userId={userId} />} />
				<Route path="active-courses" element={<UserActiveCoursesTab userId={userId} />} />
				{playlistsEnabled && (
					<Route path="active-playlists" element={<UserActivePlaylistsTab userId={userId} />} />
				)}
				<Route path="finished-courses" element={<UserFinishedCoursesTab userId={userId} />} />
			</Routes>
		</Box>
	);
};

